// src/components/BusStopManager.js

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosConfig'; // Assuming correct path
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, IconButton, Tooltip, CircularProgress,
    Snackbar, Alert, Stack, Skeleton, useTheme, alpha, // Added MUI Skeleton, theme, alpha
    DialogContentText, AlertTitle, // Added for Dialogs and Alerts
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteIcon, // Use Outlined
    MyLocation as MyLocationIcon, Search as SearchIcon, LocationOn as LocationOnIcon,
    WarningAmber as WarningIcon, // For delete confirmation
} from '@mui/icons-material';
import {
    MapContainer, TileLayer, Marker, useMapEvents, useMap, // Keep leaflet imports
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css'; // Import leaflet CSS
import NoResultsImage from '../../assets/no-results.png';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';


// Fix for React Leaflet icon issue (keep this)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// --- Helper Components (for Leaflet integration - keep these) ---
const ChangeMapView = ({ center, zoom }) => {
    const map = useMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (center && zoom && map) { // Check if map instance exists
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);
    return null;
};

const MapClickHandler = ({ onMapClick }) => { // Pass handler down
    useMapEvents({
        click: (e) => {
            if (onMapClick) { // Call parent handler
                onMapClick(e);
            }
        },
    });
    return null;
};

const ListCard = styled(AdminPanelCard)(({ theme }) => ({
    borderRadius: 6,
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 6,
    border: 'none',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
        color: '#64748b',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
        borderLeft: 'none',
        borderRight: 'none',
        fontSize: '0.92rem',
        paddingTop: theme.spacing(1.6),
        paddingBottom: theme.spacing(1.6),
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: '#f8fbff',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
        borderBottom: 'none',
    },
}));
// --- Main Component ---

const BusStopManager = () => {
    const theme = useTheme();

    // --- State Variables ---
    const [busStops, setBusStops] = useState([]);
    const [formBusStopData, setFormBusStopData] = useState({ // Renamed state
        name: '', latitude: '', longitude: '', address: '', description: '',
    });
    const [formErrors, setFormErrors] = useState({ name: '', latitude: '', longitude: '' });
    const [editBusStopId, setEditBusStopId] = useState(null);
    const [isLoading, setIsLoading] = useState(true); // Main loading state
    const [isSubmitting, setIsSubmitting] = useState(false); // Form/delete submission loading
    const [searchLoading, setSearchLoading] = useState(false); // Geocoding search loading
    const [error, setError] = useState(null); // Major fetch errors
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]); // Default center (India)
    const [mapZoom, setMapZoom] = useState(5); // Default zoom
    const [searchQuery, setSearchQuery] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
    const [busStopToDeleteId, setBusStopToDeleteId] = useState(null);

    const schoolId = localStorage.getItem('schoolId'); // Keep unchanged

    // --- Effects ---
    useEffect(() => {
        if (schoolId) {
            fetchBusStops();
            getCurrentLocation(); // Attempt to get location on mount
        } else {
            setError("School ID not found. Please log in again.");
            setIsLoading(false);
        }
    }, [schoolId]); // Add schoolId dependency

    // --- Utility Functions ---
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Format address from Nominatim API response
    const formatAddressFromNominatim = (address) => {
        if (!address) return '';
        // Prioritize more specific fields, adjust order as needed
        const parts = [
            address.road, address.neighbourhood, address.suburb, address.village,
            address.town, address.city, address.county, address.state, address.country
        ];
        return parts.filter(Boolean).join(', ');
    };

    // --- Data Fetching & Geocoding ---
    const fetchBusStops = useCallback(async () => {
        if (!schoolId) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/bus-stops', { headers: { schoolId } }); // Assuming API requires schoolId
            setBusStops(response.data || []);
        } catch (err) {
            console.error('Error fetching bus stops:', err);
            setError('Failed to fetch bus stops. Please try refreshing.');
        } finally {
            setIsLoading(false);
        }
    }, [schoolId]);

    const reverseGeocode = useCallback(async (latitude, longitude) => {
        setSearchLoading(true); // Indicate loading during reverse geocode
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
                params: { format: 'jsonv2', lat: latitude, lon: longitude, addressdetails: 1 },
            });
            return formatAddressFromNominatim(response.data?.address);
        } catch (error) {
            console.error('Reverse geocoding error:', error);
            showSnackbar('Could not determine address from coordinates.', 'warning');
            return ''; // Return empty string on error
        } finally {
             setSearchLoading(false);
        }
    }, []);

    const geocodeSearch = useCallback(async (query) => {
        if (!query.trim()) {
            showSnackbar('Please enter a location name to search.', 'info');
            return;
        }
        setSearchLoading(true);
        setError(null);
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                params: { q: query, format: 'jsonv2', addressdetails: 1, limit: 1 },
            });
            if (response.data?.length > 0) {
                const { lat, lon, address } = response.data[0];
                const latitude = parseFloat(lat);
                const longitude = parseFloat(lon);
                if (!isNaN(latitude) && !isNaN(longitude)) {
                    setMapCenter([latitude, longitude]);
                    setMapZoom(15);
                    const formattedAddress = formatAddressFromNominatim(address);
                    setFormBusStopData((prev) => ({
                        ...prev,
                        latitude: latitude.toFixed(6),
                        longitude: longitude.toFixed(6),
                        address: formattedAddress, // Update address field
                    }));
                    showSnackbar('Location found and map updated.', 'success');
                } else { throw new Error('Invalid coordinates from API.'); }
            } else {
                showSnackbar('No results found for the search query.', 'info');
            }
        } catch (err) {
            console.error('Geocoding search error:', err);
            showSnackbar('Failed to search for the location.', 'error');
        } finally {
            setSearchLoading(false);
        }
    }, []);

    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            showSnackbar('Geolocation is not supported by your browser.', 'warning');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setMapCenter([latitude, longitude]);
                setMapZoom(15);
                const address = await reverseGeocode(latitude, longitude);
                setFormBusStopData((prev) => ({
                    ...prev,
                    latitude: latitude.toFixed(6),
                    longitude: longitude.toFixed(6),
                    address: address, // Set address from current location
                }));
            },
            (error) => {
                console.error('Error getting current location:', error);
                showSnackbar(`Unable to retrieve location: ${error.message}`, 'warning');
            }
        );
    }, [reverseGeocode]); // Add dependency

    // --- Form Handlers ---
    const handleOpenFormDialog = (busStop = null) => {
        setEditBusStopId(busStop ? busStop.busStopId : null);
        setFormBusStopData(busStop ? {
            name: busStop.name || '', latitude: busStop.latitude || '', longitude: busStop.longitude || '',
            address: busStop.address || '', description: busStop.description || '',
        } : { name: '', latitude: '', longitude: '', address: '', description: '' });

        // Set map center based on existing stop or try current location
        if (busStop?.latitude && busStop?.longitude) {
            setMapCenter([parseFloat(busStop.latitude), parseFloat(busStop.longitude)]);
            setMapZoom(15);
        } else {
            // Optionally recenter map on add, perhaps using last known center or current location attempt
             // getCurrentLocation(); // Or use a default center
        }
        setFormErrors({ name: '', latitude: '', longitude: '' });
        setOpenFormDialog(true);
    };

    const handleCloseFormDialog = () => {
        setOpenFormDialog(false);
        setEditBusStopId(null);
        setSearchQuery(''); // Clear search query on dialog close
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormBusStopData((prev) => ({ ...prev, [name]: value }));
        // Clear validation error for the specific field on change
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    // Handle map click to update form fields
    const handleMapClick = useCallback(async (e) => {
        const { lat, lng } = e.latlng;
        const address = await reverseGeocode(lat, lng); // Get address from clicked point
        setFormBusStopData((prev) => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
            address: address, // Update address based on map click
        }));
        setMapCenter([lat, lng]); // Recenter map
        // Optionally clear errors if coordinates are now valid
        setFormErrors((prev) => ({ ...prev, latitude: '', longitude: '' }));
    }, [reverseGeocode]); // Add dependency

    const handleSearchButtonClick = () => {
        geocodeSearch(searchQuery);
    };

    // Form validation
    const validateForm = () => {
        let isValid = true;
        const errors = { name: '', latitude: '', longitude: '' };
        if (!formBusStopData.name.trim()) {
            errors.name = 'Bus stop name is required.';
            isValid = false;
        }
        const lat = parseFloat(formBusStopData.latitude);
        if (isNaN(lat) || lat < -90 || lat > 90) {
            errors.latitude = 'Enter a valid latitude (-90 to 90).';
            isValid = false;
        }
        const lon = parseFloat(formBusStopData.longitude);
        if (isNaN(lon) || lon < -180 || lon > 180) {
            errors.longitude = 'Enter a valid longitude (-180 to 180).';
            isValid = false;
        }
        setFormErrors(errors);
        return isValid;
    };

    // Handle form submission (Add/Update)
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setError(null);
        const apiData = { // Ensure correct data types for API
             ...formBusStopData,
             latitude: parseFloat(formBusStopData.latitude),
             longitude: parseFloat(formBusStopData.longitude),
        };

        try {
            if (editBusStopId) {
                await axios.put(`/api/bus-stops/${editBusStopId}`, apiData, { headers: { schoolId } });
                showSnackbar('Bus stop updated successfully.');
            } else {
                await axios.post('/api/bus-stops', apiData, { headers: { schoolId } });
                showSnackbar('Bus stop added successfully.');
            }
            handleCloseFormDialog();
            await fetchBusStops(); // Refresh list
        } catch (err) {
            console.error(`Error ${editBusStopId ? 'updating' : 'adding'} bus stop:`, err);
            showSnackbar(`Failed to ${editBusStopId ? 'update' : 'add'} bus stop. ${err.response?.data?.message || ''}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Delete Handlers ---
    const handleOpenDeleteConfirm = (id) => {
        setBusStopToDeleteId(id);
        setOpenDeleteConfirmDialog(true);
    };

    const handleCloseDeleteConfirm = () => {
        setBusStopToDeleteId(null);
        setOpenDeleteConfirmDialog(false);
    };

    const handleDeleteBusStop = async () => {
        if (!busStopToDeleteId) return;
        setIsSubmitting(true);
        try {
            await axios.delete(`/api/bus-stops/${busStopToDeleteId}`, { headers: { schoolId } });
            setBusStops((prev) => prev.filter((stop) => stop.busStopId !== busStopToDeleteId));
            showSnackbar('Bus stop deleted successfully.');
            handleCloseDeleteConfirm();
        } catch (err) {
            console.error('Error deleting bus stop:', err);
            showSnackbar(`Failed to delete bus stop. ${err.response?.data?.message || ''}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render Logic ---

    const renderSkeletons = (count = 5) => (
        Array.from(new Array(count)).map((_, index) => (
            <TableRow key={`skel-stop-${index}`}>
                <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={70} /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={70} /></TableCell>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                <TableCell align="center">
                    <Skeleton variant="circular" width={30} height={30} sx={{ display: 'inline-block', mr: 1 }}/>
                    <Skeleton variant="circular" width={30} height={30} sx={{ display: 'inline-block' }}/>
                </TableCell>
            </TableRow>
        ))
    );

    const renderNoBusStops = () => (
         <TableRow>
            <TableCell colSpan={6} align="center" sx={{ borderBottom: 'none', p: 0 }}>
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    py: 6, px: 3, textAlign: 'center',
                    borderRadius: 1, mt: 2, mx: 'auto', maxWidth: 'sm'
                }}>
                    {/* Consider using an appropriate icon instead of image if preferred */}
                    {/* <LocationOffIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} /> */}
                     <img src={NoResultsImage} alt="No Bus Stops" style={{ width: '120px', opacity: 0.6, marginBottom: theme.spacing(2) }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No bus stops defined yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Add bus stops to manage routes and transportation.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenFormDialog()}
                    >
                        Add Bus Stop
                    </Button>
                </Box>
            </TableCell>
        </TableRow>
    );


    return (
        <Box sx={{ width: '100%' }}>
            <AdminPageHero
                title="Bus Stop Management"
                subtitle="Manage pickup points, map coordinates, and stop details for transportation routes."
                metrics={[
                    { label: 'Stops', value: busStops.length },
                ]}
                actions={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenFormDialog()}
                        disabled={isLoading}
                    >
                        Add New Stop
                    </Button>
                }
            />

            {/* Major Error Display */}
             {error && !isLoading && (
                 <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchBusStops}>Retry</Button>}>
                     <AlertTitle>Error</AlertTitle>
                     {error}
                 </Alert>
            )}

            <Box sx={{ mt: 3 }}>
             <ListCard
                title="Bus Stops"
                subtitle="Review coordinates, addresses, and stop metadata in one place."
             >
                <SoftTableContainer>
                    <Table aria-label="bus stop management table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="center">Latitude</TableCell>
                                <TableCell align="center">Longitude</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                renderSkeletons()
                            ) : busStops.length > 0 ? (
                                busStops.map((busStop) => (
                                    <TableRow
                                        key={busStop.busStopId}
                                        hover
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <LocationOnIcon color="action" sx={{ opacity: 0.7 }}/>
                                                <Typography variant="body2" fontWeight={500}>{busStop.name}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">{busStop.latitude}</TableCell>
                                        <TableCell align="center">{busStop.longitude}</TableCell>
                                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <Tooltip title={busStop.address || '-'}>
                                                <span>{busStop.address || '-'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <Tooltip title={busStop.description || '-'}>
                                                <span>{busStop.description || '-'}</span>
                                            </Tooltip>
                                         </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Edit Stop">
                                                <IconButton size="small" color="primary" onClick={() => handleOpenFormDialog(busStop)}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Stop">
                                                <IconButton size="small" color="error" onClick={() => handleOpenDeleteConfirm(busStop.busStopId)}>
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                renderNoBusStops()
                            )}
                        </TableBody>
                    </Table>
                </SoftTableContainer>
             </ListCard>
             </Box>

            {/* Add/Edit Bus Stop Dialog */}
            <Dialog open={openFormDialog} onClose={handleCloseFormDialog} maxWidth="md" fullWidth>
                 <DialogTitle sx={{ fontWeight: 500 }}>
                     {editBusStopId ? 'Edit Bus Stop' : 'Add New Bus Stop'}
                 </DialogTitle>
                 <Box component="form" onSubmit={handleFormSubmit} noValidate>
                    <DialogContent>
                        <Grid container spacing={3}> {/* Increased spacing */}
                            {/* Left Side: Form Fields */}
                            <Grid item xs={12} md={6}>
                                <Stack spacing={2.5}> {/* Consistent spacing with Stack */}
                                    <TextField
                                        label="Bus Stop Name" name="name" value={formBusStopData.name} onChange={handleFieldChange}
                                        required fullWidth autoFocus size="small" error={!!formErrors.name} helperText={formErrors.name}/>
                                    <TextField
                                        label="Latitude" name="latitude" value={formBusStopData.latitude} onChange={handleFieldChange}
                                        fullWidth required type="number" size="small"
                                        inputProps={{ step: 'any' }} // Allows decimals
                                        placeholder="Click map or enter e.g., 12.9716"
                                        error={!!formErrors.latitude} helperText={formErrors.latitude}/>
                                    <TextField
                                        label="Longitude" name="longitude" value={formBusStopData.longitude} onChange={handleFieldChange}
                                        fullWidth required type="number" size="small"
                                        inputProps={{ step: 'any' }}
                                        placeholder="Click map or enter e.g., 77.5946"
                                        error={!!formErrors.longitude} helperText={formErrors.longitude}/>
                                    <TextField
                                        label="Address (Optional, auto-filled from map/search)" name="address" value={formBusStopData.address} onChange={handleFieldChange}
                                        fullWidth size="small" multiline rows={2}/>
                                    <TextField
                                        label="Description (Optional)" name="description" value={formBusStopData.description} onChange={handleFieldChange}
                                        fullWidth size="small" multiline rows={2}/>
                                     {/* Search Bar */}
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            label="Search Location/Village" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                            fullWidth size="small"
                                            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchButtonClick(); } }}/>
                                        <Tooltip title="Search Location">
                                             <span> {/* Span needed for disabled button tooltip */}
                                                <IconButton color="primary" onClick={handleSearchButtonClick} disabled={searchLoading}>
                                                    {searchLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                                                </IconButton>
                                             </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* Right Side: Map */}
                            <Grid item xs={12} md={6}>
                                <Stack spacing={2} sx={{ height: '100%' }}>
                                     <Typography variant="caption" color="text.secondary">
                                        Click on the map to set Latitude/Longitude or use the search.
                                     </Typography>
                                    <Box sx={{ height: { xs: 300, md: 'calc(100% - 80px)' }, minHeight: 300, borderRadius: 1, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
                                        <MapContainer
                                            center={mapCenter}
                                            zoom={mapZoom}
                                            style={{ height: '100%', width: '100%' }}
                                            scrollWheelZoom={true} // Enable scroll wheel zoom
                                        >
                                            <TileLayer
                                                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {/* Marker logic remains the same */}
                                            {formBusStopData.latitude && formBusStopData.longitude &&
                                                !isNaN(parseFloat(formBusStopData.latitude)) && !isNaN(parseFloat(formBusStopData.longitude)) && (
                                                <Marker position={[parseFloat(formBusStopData.latitude), parseFloat(formBusStopData.longitude)]} />
                                            )}
                                            <ChangeMapView center={mapCenter} zoom={mapZoom} />
                                            <MapClickHandler onMapClick={handleMapClick} /> {/* Pass handler */}
                                        </MapContainer>
                                    </Box>
                                    {/* Get Current Location Button */}
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<MyLocationIcon />}
                                        onClick={getCurrentLocation}
                                        size="small"
                                    >
                                        Use My Current Location
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={handleCloseFormDialog} color="inherit" variant="outlined">Cancel</Button>
                        <Button type="submit" color="primary" variant="contained" disabled={isSubmitting || searchLoading}>
                             {isSubmitting ? <CircularProgress size={22} color="inherit"/> : (editBusStopId ? 'Update Stop' : 'Add Stop')}
                        </Button>
                    </DialogActions>
                 </Box>
            </Dialog>

             {/* Delete Confirmation Dialog */}
             <Dialog open={openDeleteConfirmDialog} onClose={handleCloseDeleteConfirm} maxWidth="xs">
                 <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <WarningIcon color="warning"/> Confirm Deletion
                 </DialogTitle>
                 <DialogContent>
                     <DialogContentText>
                         Are you sure you want to delete this bus stop? Routes using this stop may be affected.
                     </DialogContentText>
                 </DialogContent>
                 <DialogActions>
                     <Button onClick={handleCloseDeleteConfirm} color="inherit">Cancel</Button>
                     <Button onClick={handleDeleteBusStop} color="error" variant="contained" disabled={isSubmitting}>
                          {isSubmitting ? <CircularProgress size={22} color="inherit"/> : 'Delete'}
                     </Button>
                 </DialogActions>
             </Dialog>


            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    elevation={6}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BusStopManager;
